/**
 * Sanitize and Highlight HTML
 *
 * Content viewer utility for sanitizing and highlighting HTML content.
 * This is specifically for agent references.
 *
 * Takes raw HTML and an array of sentence strings, sanitizes the HTML,
 * and applies highlights using <mark class="ref-highlight"> tags.
 */

import sanitizeHtml from 'sanitize-html'
import { kSanitizationAllowedTags } from '@/constants/constants-ui'
import { kAgentLegalCitationLinkHrefRegex } from '@/util/agent-citations'
import htmlTokenSequenceHighlighter from '@/util/html-processing/html-token-sequence-highlighter'
import { nanoid } from 'nanoid'
import { normalizeLrrLists } from './normalize-lrr-lists'

export type ProviderCitationHint = {
  locator: string
  pinpoint?: string
}

export const kProviderCitationHintAttribute = 'data-paxton-citation-hint'

/**
 * Decode HTML entities in a string using browser's native parser
 *
 * This handles all HTML entities (named and numeric) that might exist
 * in backend sentences, ensuring they match with the sanitized HTML content.
 *
 * Examples:
 * - "&quot;" → '"'
 * - "&#8220;" → '"' (smart quote)
 * - "&amp;" → '&'
 * - "&#39;" → "'"
 *
 * @param text - Text that may contain HTML entities
 * @returns Decoded text with entities converted to their character equivalents
 */
function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}

/**
 * Sanitize and highlight HTML content
 *
 * @param htmlString - Raw HTML content from API
 * @param sentences - Array of sentence strings to highlight (from highlighted_citations[].sentences)
 * @returns Sanitized HTML with highlights applied
 */
export function sanitizeAndHighlightHtml(htmlString: string, sentences?: string[]): string {
  return applyHighlights(sanitizeProviderHtml(htmlString), sentences)
}

/** Sanitize untrusted provider HTML before any frontend-owned annotations are added. */
export function sanitizeProviderHtml(htmlString: string): string {
  return sanitizeSourceHtml(htmlString)
}

/**
 * Sanitize caselaw provider HTML while preserving recognized Casetext raw-ref
 * identity as inert, unforgeable hints for the runtime citation resolver.
 * Every provider anchor is converted to a span; no provider href survives.
 */
export function sanitizeProviderHtmlWithCitationHints(htmlString: string): {
  sanitizedHtml: string
  citationHints: Map<string, ProviderCitationHint>
} {
  const citationHints = new Map<string, ProviderCitationHint>()
  return { sanitizedHtml: sanitizeSourceHtml(htmlString, citationHints), citationHints }
}

/** One normalization and sanitization policy for source viewing and downloads. */
function sanitizeSourceHtml(htmlString: string, citationHints?: Map<string, ProviderCitationHint>): string {
  return sanitizeHtml(normalizeExplicitSourceHtml(htmlString), {
    allowedTags: citationHints ? [...kSanitizationAllowedTags, 'a'] : kSanitizationAllowedTags,
    allowedAttributes: {
      span: [
        'id',
        'page-number',
        'pagination-id',
        'data-citation-type',
        'data-citation',
        'data-page',
        ...(citationHints ? [kProviderCitationHintAttribute] : []),
      ],
      div: ['data-caselaw-inline-document', 'data-lrr-list', 'data-lrr-list-item'],
      aside: ['data-caselaw-footnote'],
      sup: ['data-caselaw-footnote-ref'],
    },
    transformTags: {
      pre: 'p',
      code: 'p',
      span: (_tagName: string, attributes: Record<string, string>): sanitizeHtml.Tag => ({
        tagName: 'span',
        attribs: copyAllowedProviderSpanAttributes(attributes),
      }),
      ...(citationHints
        ? {
            // Only transformed raw-ref anchors can mint tokens in the out-of-band map.
            a: (_tagName: string, attributes: Record<string, string>): sanitizeHtml.Tag => {
              const providerHint = extractCasetextCitationHint(attributes)
              if (!providerHint) return { tagName: 'span', attribs: {} }

              const hintId = nanoid()
              citationHints.set(hintId, providerHint)
              return { tagName: 'span', attribs: { [kProviderCitationHintAttribute]: hintId } }
            },
          }
        : {}),
    },
  })
}

function copyAllowedProviderSpanAttributes(attributes: Record<string, string>): sanitizeHtml.Attributes {
  const allowed: sanitizeHtml.Attributes = {}
  for (const attribute of ['id', 'page-number', 'pagination-id']) {
    if (attributes[attribute] !== undefined) allowed[attribute] = attributes[attribute]
  }

  const page = attributes['page-number']?.trim()
  const citation = attributes['data-citation']
  if (attributes['data-citation-type'] === 'slip' && citation && attributes['data-page'] === page && attributes['pagination-id'] === citation) {
    allowed['data-citation-type'] = 'slip'
    allowed['data-citation'] = citation
    allowed['data-page'] = page
  }
  return allowed
}

function extractCasetextCitationHint(attributes: Record<string, string>): ProviderCitationHint | null {
  const classes = (attributes.class ?? '').split(/\s+/u)
  if (!classes.includes('raw-ref')) return null

  const href = attributes.href ?? ''
  if (href.startsWith('/case/')) {
    const [locator, pinpoint] = href.slice('/case/'.length).split('#', 2)
    if (!isSafeCasetextLocator(locator, false)) return null
    return { locator, pinpoint: pinpoint || undefined }
  }

  for (const lawType of ['rule/', 'regulation/', 'statute/']) {
    if (!href.startsWith(`/${lawType}`)) continue
    const rawLocator = href.replace(lawType, '')
    const [locator, pinpoint] = rawLocator.split('#', 2)
    if (!isSafeCasetextLocator(locator, true)) return null
    return { locator, pinpoint: pinpoint || undefined }
  }

  return null
}

function isSafeCasetextLocator(locator: string, allowLeadingSlash: boolean): boolean {
  if (!locator || locator.includes('..') || locator.includes('?') || locator.includes('\\')) return false
  const locatorPattern = allowLeadingSlash ? /^\/[A-Za-z0-9][A-Za-z0-9/_-]*$/u : /^[A-Za-z0-9][A-Za-z0-9/_-]*$/u
  return locatorPattern.test(locator)
}

/**
 * Normalize explicit provider markup before sanitizing ordinary source HTML.
 * Each transform is shape-driven and is a no-op when its declared markup is
 * absent. Generated matter artifacts intentionally use a separate path.
 */
function normalizeExplicitSourceHtml(htmlString: string): string {
  const template = document.createElement('template')
  template.innerHTML = htmlString
  const fragment = template.content

  normalizeLrrLists(fragment)
  normalizeInlinePreElements(fragment)
  normalizeCasetextFootnotes(fragment)

  return template.innerHTML
}

function normalizeInlinePreElements(fragment: DocumentFragment): void {
  const inlinePreElements = [...fragment.querySelectorAll('pre.inline')]
  if (inlinePreElements.length === 0) return

  for (const pre of inlinePreElements) {
    const replacement = document.createDocumentFragment()
    appendReflowedProviderContent(replacement, pre)
    pre.replaceWith(replacement)
  }

  const wrapper = document.createElement('div')
  wrapper.setAttribute('data-caselaw-inline-document', '')
  wrapper.append(...fragment.childNodes)
  fragment.append(wrapper)
}

function appendReflowedProviderContent(container: DocumentFragment, pre: Element): void {
  for (const child of [...pre.childNodes]) {
    if (child.nodeType === Node.TEXT_NODE) {
      appendReflowedProviderText(container, child.textContent ?? '')
      continue
    }

    const pageMarker = copyPdfPageMarker(child)
    if (pageMarker) {
      container.append(pageMarker)
    } else {
      appendReflowedProviderText(container, child.textContent ?? '')
    }
  }
}

function copyPdfPageMarker(node: ChildNode): HTMLSpanElement | null {
  if (!(node instanceof HTMLSpanElement)) return null

  const page = node.getAttribute('page-number')?.trim()
  if (!page || !/^\d{1,5}$/.test(page)) return null

  const marker = document.createElement('span')
  marker.setAttribute('page-number', page)

  const citationType = node.getAttribute('data-citation-type')
  const citation = node.getAttribute('data-citation')
  const dataPage = node.getAttribute('data-page')
  const paginationId = node.getAttribute('pagination-id')
  if (citationType === 'slip' && citation && dataPage === page && paginationId === citation) {
    marker.setAttribute('data-citation-type', 'slip')
    marker.setAttribute('data-citation', citation)
    marker.setAttribute('data-page', page)
    marker.setAttribute('pagination-id', citation)
  }

  return marker
}

function appendReflowedProviderText(container: DocumentFragment, text: string): void {
  const normalizedNewlines = text.replace(/\r\n?/g, '\n').replace(/\f/g, '\n\n\n')
  const paragraphs = normalizedNewlines.split(/\n{3,}/)

  for (const [index, paragraph] of paragraphs.entries()) {
    if (index > 0) {
      container.append(document.createElement('br'), document.createElement('br'))
    }

    container.append(paragraph.replace(/[ \t]*\n+[ \t]*/g, ' '))
  }
}

function normalizeCasetextFootnotes(fragment: DocumentFragment): void {
  for (const existingMarker of fragment.querySelectorAll('sup.note-ref')) {
    const label = existingMarker.textContent?.trim() ?? ''

    const marker = document.createElement('sup')
    marker.setAttribute('data-caselaw-footnote-ref', '')
    marker.textContent = label
    existingMarker.replaceWith(marker)
  }

  for (const disclosure of fragment.querySelectorAll('a[note-disclosure]')) {
    const label = disclosure.getAttribute('note-disclosure')?.trim() ?? ''

    const marker = document.createElement('sup')
    marker.setAttribute('data-caselaw-footnote-ref', '')
    marker.textContent = label
    disclosure.replaceWith(marker)
  }

  for (const container of fragment.querySelectorAll('[note-container]')) {
    const label = container.getAttribute('note-container')?.trim() ?? ''
    const footnote = document.createElement('aside')
    footnote.setAttribute('data-caselaw-footnote', '')

    if (label) {
      const marker = document.createElement('sup')
      marker.setAttribute('data-caselaw-footnote-ref', '')
      marker.textContent = label
      footnote.append(marker, ' ')
    }

    footnote.append(...container.childNodes)
    container.replaceWith(footnote)
  }
}

/**
 * Sanitize and highlight a read-only artifact's rendered markdown.
 *
 * Same strict policy as `sanitizeAndHighlightHtml`, with one artifact-specific
 * difference: an <a> whose href is a bare citation token ("ref_" + 6 or 9
 * alphanumerics — the shape markdown rendering gives a legal citation link)
 * survives with only its href. Every other anchor becomes a plain <span>
 * (text preserved, link removed). The artifact view's citation post-pass rewrites the kept
 * anchors to reference-viewer links or unwraps them — nothing user-navigable
 * survives sanitization as-is.
 */
export function sanitizeAndHighlightArtifactHtml(htmlString: string, sentences?: string[]): string {
  const sanitizedHtml = sanitizeHtml(htmlString, {
    allowedTags: [...kSanitizationAllowedTags, 'a'],
    allowedAttributes: {
      span: ['id', 'page-number', 'pagination-id'],
      a: ['href'],
    },
    transformTags: {
      pre: 'p',
      code: 'p',
      a: (tagName: string, attribs: Record<string, string>): sanitizeHtml.Tag =>
        kAgentLegalCitationLinkHrefRegex.test(attribs.href ?? '')
          ? { tagName, attribs: { href: attribs.href } }
          : { tagName: 'span', attribs: {} as sanitizeHtml.Attributes },
    },
  })

  return applyHighlights(sanitizedHtml, sentences)
}

/**
 * Wrap the given sentences in <mark class="ref-highlight"> tags within the HTML.
 */
export function applyHighlights(sanitizedHtml: string, sentences?: string[]): string {
  // If no sentences are provided, return the sanitized HTML
  if (!sentences || sentences.length === 0) {
    return sanitizedHtml
  }

  // Decode HTML entities in sentences to ensure they match with sanitized HTML
  // Backend sentences may contain entities like &quot; &#8220; that need to be decoded
  const decodedSentences = sentences.map(decodeHtmlEntities)

  // Add <mark class="ref-highlight"> tags to the sanitized HTML
  return htmlTokenSequenceHighlighter(sanitizedHtml, decodedSentences)
}
